/* eslint-disable ember/classic-decorator-no-classic-methods */
import AttendanceCalendarDays from 'linced-app/components/attendance-calendar-days';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import EmberObject, { computed } from '@ember/object';
// Extends From
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import AttendanceCalendar from 'linced-app/components/attendance-calendar';

export default class AttendanceCalendarTerm extends AttendanceCalendar {
  // Component class rather than a string: a string here reaches
  // `ensureSafeComponent` and trips `ensure-safe-component.string`.
  daysComponent = AttendanceCalendarDays;

  // Services
  @service
  attendanceCodes;

  @service
  dates;

  // Defaults
  // term is the term object for the school term to be displayed.
  term = null;

  // records are the attendance records for this term
  records = null;

  dayCodes = null;
  @tracked trackMovements = false;
  Jurisdiction = Jurisdiction;

  @computed(
    'records.[]',
    'trackMovements',
    'attendanceCodes.{allCodes.[],truancyClasses}',
  )
  get attendanceByDate() {
    if (isEmpty(this.records)) {
      return {};
    }
    return this.records.reduce((byDate, record) => {
      // New object if the date doesn't exist yet
      if (isEmpty(byDate[record.date])) {
        byDate[record.date] = {};
      }
      // Retrieve the truancy label and code
      let code = findBy(this.attendanceCodes.allCodes, 'code', record.code);
      if (isEmpty(code)) {
        // Code not found - likely a note-only attendance record, or the
        // code has been cleared.  Ignore this record.
        return byDate;
      }
      let { label, truancy } = code;
      if (this.trackMovements && record.movements.length > 1) {
        // For records with more than 1 attendance movement
        label += `. Last movement shown, more exist for the day.`;
      }
      let codeClass = this.get(`attendanceCodes.truancyClasses.${record.code}`);
      // Populate object with the data for a period
      byDate[record.date][record.period_id] = EmberObject.create({
        code: record.code,
        truancyCode: truancy,
        className: codeClass,
        label: `(${record.code}) - ${label}`,
      });
      return byDate;
    }, {});
  }

  @computed('dayCodes.[]')
  get attendanceByDateDayCodes() {
    if (isEmpty(this.dayCodes)) {
      return {};
    }
    return this.dayCodes.reduce((byDate, dayCode) => {
      // Initialize an array to store dates
      byDate[dayCode.date] = [];

      if (isEmpty(dayCode.code)) {
        // Code not found - likely a note-only attendance record, or the
        // code has been cleared.  Ignore this record.
        return byDate;
      }

      const pushCell = (className, label) => {
        let cell = EmberObject.create({
          date: dayCode.date,
          className: className,
          label,
        });
        byDate[dayCode.date].push(cell);
        return cell;
      };

      let presentCells = [];
      let absenceCells = [];

      if (dayCode.code === 'M' || dayCode.code === 'H') {
        pushCell(
          this.getTruancyClassByDayCode(dayCode.code),
          dayCode.code === 'M' ? 'Exempted' : 'Shared Enrolment',
        );
      }

      for (let i = 0; i < dayCode.exempt; i++) {
        pushCell(
          this.getTruancyClassByDayCode(dayCode.exemptCode),
          dayCode.exemptCode === 'M' ? 'Exempted' : 'Shared Enrolment',
        );
      }
      for (let i = 0; i < dayCode.present; i++) {
        presentCells.push(
          pushCell(
            this.getTruancyClassByDayCode('P'),
            this.attendanceCodes.getLabelForTruancyCode('P'),
          ),
        );
      }
      for (let i = 0; i < dayCode.unjustifiablyAbsent; i++) {
        absenceCells.push(
          pushCell(
            this.getTruancyClassByDayCode('U'),
            this.attendanceCodes.getLabelForTruancyCode('U'),
          ),
        );
      }
      for (let i = 0; i < dayCode.justifiedAbsent; i++) {
        absenceCells.push(
          pushCell(
            this.getTruancyClassByDayCode('J'),
            this.attendanceCodes.getLabelForTruancyCode('J'),
          ),
        );
      }
      if (dayCode.hasMinorPartialAbsence) {
        let targetCell = presentCells[0] || absenceCells[0];
        if (targetCell) {
          targetCell.set(
            'className',
            `${targetCell.className} attendance-late`,
          );
          targetCell.set('label', 'Partially absent');
        } else {
          pushCell('attendance-late', 'Partially absent');
        }
      }
      return byDate;
    }, []);
  }

  getTruancyClassByDayCode(dayCode) {
    switch (dayCode) {
      case 'M':
      case 'H':
      case 'I':
      case 'P':
        return 'attendance-present';
      case 'U':
        return 'attendance-unjustified';
      case 'J':
      case 'O':
        return 'attendance-justified';
      default:
        return '';
    }
  }
}
